// --------------------------------------------------------
// CRIAR AGENDA - CriarAgenda
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import CriarAgendaHeader from '../../components/criarAgendaHeader/CriarAgendaHeader';
import ContainerWithSidebar from '../../components/UI/containerWithSidebar/ContainerWithSidebar';
import { GeneralContainer, H1, ResponsiveContainer, Text, TextLink } from '../../components/UI/generalLayout/Layout.styled';
import BackButton from '../../components/UI/buttons/BackButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import '@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css';
import { Calendar, Day, utils } from '@hassanmojab/react-modern-calendar-datepicker';
import { proporcionalidadeOptions, ptBrCustomLocale, toleranciaOptions } from '../../constants/Objects';
import { Checkbox, CircularProgress, Modal, TextField } from '@mui/material';
import Colors from '../../constants/Colors';
import CareLineDropdown from '../../components/dropdown/CareLineDropdown';
import { GenericSolidButton } from '../../components/UI/buttons/Button.styled';
import useResponsiveInfo from '../../hooks/useResponsiveInfo';
import { getDates, getHours, handleBackendErrors, isEmptyObject } from '../../helpers/HelperFunctions';
import { Moment } from 'moment';
import AgendaDiaHorarioListItem from '../../components/agendaDiaHorarioListItem/AgendaDiaHorarioListItem';
import { AgendaFieldsContainer, BottomButtonContainer, CalendarContainer, ConsiderWeekendContainer, CriarAgendaText, GrayText, LoadingModalStyled, ResponsiveContainerCriarAgenda } from './CriarAgenda.styled';
import { useSelector } from 'react-redux';
import Unit from '../../models/core/unit/Unit';
import InputMask from "react-input-mask";
import moment from 'moment-timezone';
import lodash from 'lodash';
import { Paths } from '../../enums/Paths';
import * as agendaActions from '../../store/actions/agenda';
import * as unitActions from '../../store/actions/unit';
import { useAppDispatch } from '../..';
import SuccessModal from '../../components/successModal/SuccessModal';
import { ReactComponent as CalendarCheck } from '../../assets/imgs/calendarCheck.svg';
import { AgendaService } from '../../core/http/service/AgendaService';
import CriarAgendaHorarioInput from '../../components/criarAgenda/CriarAgendaHorarioInput';

// --------------------------------------------------------
// CRIAR AGENDA INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function CriarAgenda(): JSX.Element {
    // --------------------------------------------------------
    // CRIAR AGENDA PRIVATE DECLARATIONS
    // --------------------------------------------------------
    const { state }: any = useLocation();
    const { nomeProfissional, agenda, updatedDaysList, savedState } = state || {};
    const { isMobile, isDesktop } = useResponsiveInfo();
    const dispatch = useAppDispatch()
    const navigate = useNavigate();

    const [dayRange, setDayRange] = useState<any>(savedState?.dayRange || { from: null, to: null })
    const [daysList, setDaysList] = useState<any[]>(updatedDaysList || [])
    const [considerSaturday, setConsiderSaturday] = useState(savedState?.considerSaturday || false)
    const [considerSunday, setConsiderSunday] = useState(savedState?.considerSunday || false)
    const [unitSelected, setUnitSelected] = useState<any>(savedState?.unitSelected || {})
    const [horarios, setHorarios] = useState<any[]>(savedState?.horarios || [{ horarioInicial: '', horarioFinal: '', tempoConsulta: '', inativo: false }])
    const [hasProporcionalidade, setHasProporcionalidade] = useState(savedState?.hasProporcionalidade || { nome: 'Não', id: 'N' })
    const [unitList, setActiveUnits] = useState<any[]>([])
    const [proporcClinico, setPropClinico] = useState(savedState?.proporcClinico || '')
    const [proporcPeriodico, setPropPeriodico] = useState(savedState?.proporcPeriodico || '')
    const [proporcPreventivo, setPropPreventivo] = useState(savedState?.proporcPreventivo || '')
    const [tolerancia, setTolerancia] = useState<any>(savedState?.tolerancia || '')

    const isFirstRender = useRef(true);

    const onVerAgenda = (index: number) => {
        const savedState = {
            dayRange,
            considerSaturday,
            considerSunday,
            unitSelected,
            horarios,
            hasProporcionalidade,
            proporcClinico,
            proporcPeriodico,
            proporcPreventivo,
            tolerancia
        };

        navigate(Paths.AgendaPreviewDia, {
            state: {
                nomeProfissional,
                agenda,
                daysList,
                dayIndex: index,
                savedState
            }
        });
    }
    const [disableVisualizarAgenda, setDisableVisualizarAgenda] = useState<boolean>(true)
    const [proporcionalidadeError, setProporcionalidadeError] = useState('')
    const [horariosAtendimentoError, setHorariosAtendimentoError] = useState('')
    const [overlapError, setOverlapError] = useState<any[]>([])
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [generalErrors, setGeneralErrors] = useState<any[]>([])
    const [disableTolerancia, setDisableTolerancia] = useState(false)
    const [isValidatingHours, setIsValidatingHours] = useState(false)
    const [isCreating, setIsCreating] = useState(false)

    const units: Unit[] = useSelector((state: any) => state.unit.units)

    const fetchUnits = useCallback(async () => {
        await dispatch(unitActions.fetchUnitList())
    }, [])

    useEffect(() => {
        fetchUnits();
    }, [])

    useEffect(() => {
        const isRangeEmpty = dayRange.from === null || dayRange.to === null;
        const isUnitEmpty = Object.values(unitSelected).length === 0;
        const isHorariosEmpty = horarios.filter(h => {
            return h.horarioFinal === '' || h.horarioInicial === '' || h.tempoConsulta === '';
        }).length > 0;
        const isProporcionalidadeEmpty = hasProporcionalidade.id !== 'S' ? false : (proporcionalidadeError !== '' || tolerancia === '')

        if (isRangeEmpty || isUnitEmpty || isHorariosEmpty || isProporcionalidadeEmpty) {
            setDisableVisualizarAgenda(true)
        } else {
            setDisableVisualizarAgenda(false)
        }
    }, [unitSelected, horarios, proporcClinico, proporcPeriodico, proporcPreventivo, tolerancia, dayRange, hasProporcionalidade, proporcionalidadeError])

    useEffect(() => {
        const isAnyProporcionalityFilled = proporcClinico !== '' || proporcPeriodico !== '' || proporcPreventivo !== ''
        const isProporcNot100 = (Number(proporcClinico) + Number(proporcPeriodico) + Number(proporcPreventivo)) !== 100;
        if (isAnyProporcionalityFilled && isProporcNot100) {
            setProporcionalidadeError('Soma das proporções deve ser de 100%')
        } else {
            setProporcionalidadeError('')
        }
    }, [proporcClinico, proporcPeriodico, proporcPreventivo])

    useEffect(() => {
        const isAnyProporcionalityFilled = proporcClinico !== '' || proporcPeriodico !== '' || proporcPreventivo !== '';
        const isAnyProporc100 = Number(proporcClinico) === 100 || Number(proporcPeriodico) === 100 || Number(proporcPreventivo) === 100;
        if (isAnyProporcionalityFilled && isAnyProporc100) {
            if (tolerancia?.id !== '0') {
                setTolerancia({ nome: 'Sem tolerância', id: '0' })
            }
            setDisableTolerancia(true)
        } else {
            setDisableTolerancia(false)
            if (tolerancia && typeof tolerancia === 'object' && tolerancia.id === '0') {
                setTolerancia('')
            }
        }
    }, [proporcClinico, proporcPeriodico, proporcPreventivo])

    useEffect(() => {
        if (!!units) {
            const unidadesAtivas: any[] = []
            units.forEach((unit: Unit) => {
                if (!unit.inativo) {
                    unidadesAtivas.push({ nome: unit.nomeFantasia, id: unit.id })
                }
            })
            setActiveUnits(unidadesAtivas)
        }
    }, [units])

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (daysList.length > 0) {
            setDaysList([])
        }
    }, [unitSelected, horarios, dayRange, hasProporcionalidade, tolerancia, proporcClinico, proporcPeriodico, proporcPreventivo])

    useEffect(() => {
        const horariosInvalidos = horarios.filter(h => {
            const isHorarioInicialInvalido =
                (h.horarioInicial !== '' && h.horarioInicial.slice(-1) !== '0')
                || (h.horarioInicial !== '' && h.horarioInicial.length < 5)
                || (h.horarioInicial !== '' && Number(h.horarioInicial.slice(0, 2)) > 23)
            const isHorarioFinalInvalido =
                (h.horarioFinal !== '' && h.horarioFinal.slice(-1) !== '0')
                || (h.horarioFinal !== '' && h.horarioFinal.length < 5)
                || (h.horarioFinal !== '' && Number(h.horarioFinal.slice(0, 2)) > 23)
            return isHorarioInicialInvalido || isHorarioFinalInvalido;
        })

        if (horariosInvalidos.length > 0) {
            setHorariosAtendimentoError('Horários de atendimento devem ser múltiplos de 10 minutos e estarem entre 00:00 e 23:00')
            setDisableVisualizarAgenda(true)
        } else {
            setHorariosAtendimentoError('')
        }
    }, [horarios])

    useEffect(() => {
        const horariosVazios = horarios.some(r => { return r.horarioFinal === '' || r.horarioInicial === '' || isEmptyObject(r.tempoConsulta) })
        if (horariosVazios) {
            setHorariosAtendimentoError(' ')
        }
    }, [horarios])

    const validateRange = () => {
        try {
            if (dayRange.from.month !== dayRange.to.month) {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                setGeneralErrors([{ message: 'Agendas não podem começar e terminar em meses diferentes. Selecione o início e o fim da agenda dentro do mesmo mês' }])
                throw new Error()
            }
        } catch (error: any) {
            throw error;
        }
    }

    const Button = ({ label, onClick, disabled = false }: any) => {
        return (
            <GenericSolidButton
                disabled={disabled}
                type="button"
                style={{ width: !isDesktop ? '100%' : 310 }}
                onClick={onClick}
                color={Colors.SPM600}
            >
                <AddCircleOutlineIcon />{label}
            </GenericSolidButton>
        )
    }

    const getShowDay = (dateHour: any) => {
        const isSaturday = dateHour.isSaturday;
        const isSunday = dateHour.isSunday;
        let showDay = true;

        if ((!considerSaturday && isSaturday) || (!considerSunday && isSunday)) {
            showDay = false;
        }

        return showDay;
    }

    const validateSchedules = async () => {
        const service: AgendaService = new AgendaService();
        const start = new Date(dayRange.from.year, dayRange.from.month - 1, dayRange.from.day);
        const end = new Date(dayRange.to.year, dayRange.to.month - 1, dayRange.to.day);
        const listaDates = getDates(start, end)
        const busySchedules: any[] = [];
        setIsValidatingHours(true)

        for (const d of listaDates) {
            for (const h of horarios) {
                const diaFormatado = d.format('YYYY-MM-DD') + 'T' + h.horarioInicial;
                const diaFinal = moment(diaFormatado).format('YYYY-MM-DDTHH:mm:ss');
                const params = {
                    profissionalId: agenda.id,
                    duracaoEmMinutos: Number(h.tempoConsulta.id),
                    horarios: [diaFinal]
                }

                try {
                    const response: any = await service.validateBusySchedules(params)
                    const horariosEmChoque: string[] = response.data.data;
                    if (horariosEmChoque.length > 0) {
                        busySchedules.push({ data: d.format('DD/MM/YYYY'), choques: [...horariosEmChoque] })
                    }
                } catch (error: any) {
                    setIsValidatingHours(false)
                    throw error;
                }
            }
        }

        setIsValidatingHours(false)

        return busySchedules;
    }

    const onVisualizarAgenda = async () => {
        try {
            validateRange();
            const busySchedulesTest = await validateSchedules();

            if (busySchedulesTest.length === 0) {
                visualizarAgenda();
                setGeneralErrors([]);
            } else {
                let choques = '';
                busySchedulesTest.forEach(b => {
                    b.choques.forEach((c: string) => {
                        const choque = `${b.data} - ${moment.utc(c).format('HH:mm')} | \n`
                        choques += choque;
                    })
                })
                const errorMessage = 'Profissional possui horário de agenda conflitante no período:\n' + choques;

                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                setGeneralErrors([{ message: errorMessage }]);
                setDaysList([]);
            }
        } catch (error: any) {
            setDaysList([]);
            handleBackendErrors(error, setGeneralErrors)
        }
    }

    const visualizarAgenda = () => {
        const initialDate = new Date(dayRange.from.year, dayRange.from.month - 1, dayRange.from.day)
        const finalDate = new Date(dayRange.to.year, dayRange.to.month - 1, dayRange.to.day)
        const datesList: Moment[] = getDates(initialDate, finalDate);
        const datesHoursList: any[] = []

        datesList.forEach((d: Moment) => {
            horarios.forEach((horario: any) => {
                const duracaoEmMinutos = Number(horario.tempoConsulta.id);
                const horasInBetween = getHours(horario.horarioInicial, horario.horarioFinal, duracaoEmMinutos, d.format('DD/MM/YYYY'))
                const slots = horasInBetween.map(hb => {
                    const dateTime = hb.format('YYYY-MM-DDTHH:mm')
                    return { horario: dateTime, inativo: false, excluido: false }
                }).filter(slot => {
                    const proxHorario = moment(slot.horario).add(duracaoEmMinutos, 'm')
                    const horarioLimite = moment(d.format('DD/MM/YYYY') + 'T' + horario.horarioFinal, 'DD/MM/YYYYTHH:mm')
                    if (horario.horarioInicial >= horario.horarioFinal) {
                        horarioLimite.add(1, 'd')
                    }
                    return proxHorario <= horarioLimite;
                })

                datesHoursList.push({ 
                    date: d.format('DD/MM/YYYY'), 
                    horario: horario, 
                    isSaturday: d.day() === 6, 
                    isSunday: d.day() === 0, 
                    inativo: false,
                    slots: slots
                })
            })
        })

        setDaysList(datesHoursList)
    }

    const getGroupedDays = (days: any) => {
        const diasVisiveis: any[] = []
        days.forEach((day: any) => {
            const showDay = getShowDay(day);
            if (showDay) {
                diasVisiveis.push(day)
            }
        })
        return diasVisiveis;
    }

    const onCriarAgenda = async () => {
        const initialDate = new Date(dayRange.from.year, dayRange.from.month - 1, dayRange.from.day)
        const finalDate = new Date(dayRange.to.year, dayRange.to.month - 1, dayRange.to.day)
        const turnos: any[] = []
        const tiposConsulta: any[] = []

        if (proporcClinico !== '') {
            tiposConsulta.push({ tipoConsulta: 'clinica', proporcao: Number(proporcClinico) })
        }

        if (proporcPeriodico !== '') {
            tiposConsulta.push({ tipoConsulta: 'periodica', proporcao: Number(proporcPeriodico) })
        }

        if (proporcPreventivo !== '') {
            tiposConsulta.push({ tipoConsulta: 'preventiva', proporcao: Number(proporcPreventivo) })
        }

        horarios.forEach((horario: any) => {
            const turnosDia: any[] = []
            const horaInicio = horario.horarioInicial;
            const horaFim = horario.horarioFinal
            const duracaoEmMinutos = Number(horario.tempoConsulta.id)
            const diasAgrupados = Object.entries(lodash.groupBy(getGroupedDays(daysList), 'date'))

            diasAgrupados.forEach(dia => {
                const horariosDia = dia[1];
                const horariosFinal: any[] = []
                horariosDia.forEach(h => {
                    if (h.horario.horarioInicial === horaInicio && h.horario.horarioFinal === horaFim) {
                        h.slots.forEach((slot: any) => {
                            if (!slot.excluido) {
                                horariosFinal.push({
                                    horario: slot.horario,
                                    inativo: slot.inativo || h.inativo
                                })
                            }
                        })
                    }
                })
                if (horariosFinal.length > 0) {
                    const isAllInactive = horariosFinal.every(h => h.inativo);
                    const obj = { dia: moment.utc(dia[0], 'DD/MM/YYYY').format('YYYY-MM-DD'), inativo: isAllInactive, horarios: horariosFinal }
                    turnosDia.push(obj)
                }
            })
            if (turnosDia.length > 0) {
                const turno = { horaInicio: horaInicio, horaFim: horaFim, duracaoEmMinutos: duracaoEmMinutos, turnosDia: turnosDia }
                turnos.push(turno)
            }
        })

        if (turnos.length === 0) {
            setGeneralErrors([{ message: 'Insira algum dia e horário válidos' }])
        } else {
            const object = {
                profissionalId: agenda.id,
                unidadeAtendimentoId: unitSelected.id,
                perfilSaude: agenda.perfilSaude,
                dataInicio: moment(initialDate).format('YYYY-MM-DD'),
                dataFim: moment(finalDate).format('YYYY-MM-DD'),
                considerarSabados: considerSaturday,
                considerarDomingos: considerSunday,
                tiposConsulta: tiposConsulta,
                proporcionalizar: hasProporcionalidade.id === 'S',
                toleranciaEmHoras: !!tolerancia.id ? Number(tolerancia.id) * 24 : 0,
                turnos: turnos
            }
            try {
                setIsCreating(true)
                await dispatch(agendaActions.createAgenda(object))
                setShowSuccessModal(true)
            } catch (error: any) {
                handleBackendErrors(error, setGeneralErrors)
            } finally {
                setIsCreating(false)
            }
        }
    }

    const onBlockHorario = (dateHour: any, index: number) => {
        const inativo = !dateHour.inativo;
        setDaysList((prev: any) => {
            const list = [...prev];
            const removedElementList = list.splice(index, 1)
            const removedElement = removedElementList[0]
            list.splice(index, 0, { ...removedElement, inativo: inativo })
            return list;
        })
    }

    const onExcluirHorario = (dateHour: any, index: number) => {
        setDaysList((prev: any) => {
            const list = [...prev];
            list.splice(index, 1)
            return list;
        })
    }

    const onSuccessModalButtonClick = () => {
        setShowSuccessModal(false)
        return navigate(Paths.Agendas, { state: { agenda: agenda, nome: nomeProfissional } })
    }

    const getMaximumDate = () => {
        const day = utils('en').getToday();
        const momentDate = moment(`${day.year}-${day.month}-${day.day}`, 'YYYY-MM-DD').add(60, 'days')
        const maxDate: Day = { year: momentDate.year(), month: momentDate.month() + 2, day: momentDate.day() };
        return maxDate;
    }

    const LoadingModal = () => {
        return (<Modal open={isValidatingHours}>
            <LoadingModalStyled>
                <CircularProgress style={{ marginTop: 70 }} />
                <Text color={Colors.SPM600}>Validando horários</Text>
            </LoadingModalStyled>
        </Modal>)
    }

    return <ContainerWithSidebar>
        <GeneralContainer>
            <BackButton onClick={() => navigate(Paths.CollaboratorInitial)} />
            <CriarAgendaHeader
                nome={nomeProfissional}
                hasRightButton={false}
                agenda={agenda}
                errors={generalErrors}
                onCloseError={() => { setGeneralErrors([]) }} />
            <H1>Criar Agenda</H1>
            <ResponsiveContainerCriarAgenda isMobile={!isDesktop}>
                <CalendarContainer isMobile={!isDesktop}>
                    <Calendar
                        value={dayRange}
                        onChange={setDayRange}
                        minimumDate={utils('en').getToday()}
                        maximumDate={getMaximumDate()}
                        locale={ptBrCustomLocale}
                        colorPrimary={Colors.SPM600}
                        colorPrimaryLight={Colors.SPM50}
                        shouldHighlightWeekends
                    />
                    <ConsiderWeekendContainer style={{ marginTop: 20 }}>
                        <Checkbox checked={considerSaturday} onChange={() => { setConsiderSaturday((prev: boolean) => !prev) }} />
                        <CriarAgendaText>Considerar sábado</CriarAgendaText>
                    </ConsiderWeekendContainer>
                    <ConsiderWeekendContainer>
                        <Checkbox checked={considerSunday} onChange={() => { setConsiderSunday((prev: boolean) => !prev) }} />
                        <CriarAgendaText>Considerar domingo</CriarAgendaText>
                    </ConsiderWeekendContainer>
                </CalendarContainer>
                <AgendaFieldsContainer isMobile={!isDesktop}>
                    <GrayText>Selecione a unidade</GrayText>
                    <div style={{ display: 'flex' }}>
                        <CareLineDropdown containerStyle={{ width: !isDesktop ? '95%' : 340 }} value={unitSelected} onChange={setUnitSelected} items={unitList} disabled={false} />
                    </div>
                    {overlapError.length > 0 && <Text color={Colors.error}>Um ou mais horários estão se sobrepondo. Insira períodos únicos</Text>}
                    {horariosAtendimentoError !== '' && <Text color={Colors.error}>{horariosAtendimentoError}</Text>}
                    {horarios.map((h: any, index: number) => {
                        return <>
                            <CriarAgendaHorarioInput
                                horarios={horarios}
                                horario={h}
                                index={index}
                                setHorarioError={setOverlapError}
                                onChangeHorario={setHorarios}
                                onChangeTempoConsulta={setHorarios}
                                onTrashClick={setHorarios}
                            />
                        </>
                    })}

                    <Button
                        label="Adicionar horário"
                        disabled={horarios.length === 3 || horariosAtendimentoError !== '' || overlapError.length > 0}
                        onClick={() => { setHorarios([...horarios, { horarioInicial: '', horarioFinal: '', tempoConsulta: '', inativo: false }]) }}
                    />
                    <ResponsiveContainer isMobile={isMobile}>
                        <div style={{ marginRight: 12 }}>
                            <GrayText>Adicionar proporcionalidade</GrayText>
                            <CareLineDropdown containerStyle={{ width: !isDesktop ? '98%' : 310 }} value={hasProporcionalidade} onChange={setHasProporcionalidade} items={proporcionalidadeOptions} disabled={false} />
                        </div>
                        {hasProporcionalidade.id === 'S' && (
                            <ResponsiveContainer style={{ marginTop: !isDesktop ? 16 : 0 }}>
                                <div>
                                    <Text style={{ fontSize: 14, color: Colors.GRAY400 }}>Clínico</Text>
                                    <InputMask
                                        mask="999"
                                        onKeyPress={e => { e.key === 'Enter' && e.preventDefault() }}
                                        maskPlaceholder="" value={proporcClinico}
                                        onChange={(event: any) => { setPropClinico(event.target.value) }}
                                    >
                                        <TextField error={proporcionalidadeError !== ''} inputProps={{ maxLength: 3 }} style={{ marginRight: 10, width: 80 }} />
                                    </InputMask>
                                </div>
                                <div>
                                    <Text style={{ fontSize: 14, color: Colors.GRAY400 }}>Periódico</Text>
                                    <InputMask
                                        mask="999"
                                        onKeyPress={e => { e.key === 'Enter' && e.preventDefault() }}
                                        maskPlaceholder="" value={proporcPeriodico}
                                        onChange={(event: any) => { setPropPeriodico(event.target.value) }}
                                    >
                                        <TextField error={proporcionalidadeError !== ''} inputProps={{ maxLength: 3 }} style={{ marginRight: 10, width: 80 }} />
                                    </InputMask>
                                </div>
                                <div>
                                    <Text style={{ fontSize: 14, color: Colors.GRAY400 }}>Preventivo</Text>
                                    <InputMask
                                        mask="999"
                                        onKeyPress={e => { e.key === 'Enter' && e.preventDefault() }}
                                        maskPlaceholder="" value={proporcPreventivo}
                                        onChange={(event: any) => { setPropPreventivo(event.target.value) }}
                                    >
                                        <TextField error={proporcionalidadeError !== ''} inputProps={{ inputMode: 'numeric', maxLength: 3 }} style={{ marginRight: 10, width: 80 }} />
                                    </InputMask>
                                </div>
                            </ResponsiveContainer>
                        )}
                    </ResponsiveContainer>

                    {(proporcionalidadeError !== '' && hasProporcionalidade.id === 'S') && <Text color={Colors.error}>{proporcionalidadeError}</Text>}

                    {hasProporcionalidade.id === 'S' && <ResponsiveContainer>
                        <div style={{ width: '100%' }}>
                            <Text style={{ fontSize: 14, color: Colors.GRAY400 }}>Adicionar tolerância</Text>
                            <CareLineDropdown disabled={disableTolerancia} containerStyle={{ width: !isDesktop ? '95%' : 310 }} value={tolerancia} onChange={(event: any) => { setTolerancia(event) }} items={toleranciaOptions} />
                        </div>
                    </ResponsiveContainer>}

                    <GenericSolidButton
                        disabled={disableVisualizarAgenda || horariosAtendimentoError !== '' || overlapError.length > 0}
                        type="button"
                        style={{ width: !isDesktop ? '100%' : 310, marginTop: 10 }}
                        onClick={async () => { await onVisualizarAgenda() }}
                        color={Colors.SPM600}
                    >
                        <AddCircleOutlineIcon />Visualizar agenda
                    </GenericSolidButton>

                </AgendaFieldsContainer>
            </ResponsiveContainerCriarAgenda>
            <div style={{ marginTop: 10 }}>
                {daysList.length > 0 && !isMobile && (
                    <div style={{ flexDirection: 'row', display: 'flex', paddingLeft: 10, paddingRight: 10, marginBottom: 10 }}>
                        <Text bold style={{ width: 200 }}>Data</Text>
                        <Text bold style={{ width: 200 }}>Intervalo</Text>
                        <Text bold style={{ flex: 1, textAlign: 'center' }}>Agenda</Text>
                        <Text bold style={{ width: 100, textAlign: 'center' }}>Ações</Text>
                    </div>
                )}
                {daysList.map((dateHour: any, index: number) => {
                    const showDay = getShowDay(dateHour);
                    return (<>
                        {showDay &&
                            (<AgendaDiaHorarioListItem
                                dateHour={dateHour}
                                index={index}
                                onBlockHorario={() => { onBlockHorario(dateHour, index) }}
                                onConfirmDeleteHorario={() => { onExcluirHorario(dateHour, index) }}
                                onVerAgenda={() => onVerAgenda(index)}
                            />)}</>)

                })}
            </div>
            {daysList.length > 0 && (<BottomButtonContainer isMobile={!isDesktop}>
                <GenericSolidButton
                    color={Colors.SPM600}
                    disabled={isCreating}
                    type="button"
                    style={{ width: !isDesktop ? '100%' : 250, marginTop: 10 }}
                    onClick={onCriarAgenda}
                >
                    {isCreating ? <CircularProgress size={24} style={{ color: 'white' }} /> : <><AddCircleOutlineIcon />Criar Agenda</>}
                </GenericSolidButton>
            </BottomButtonContainer>)}
            <SuccessModal
                visible={showSuccessModal}
                Icon={() => { return <CalendarCheck /> }}
                onVoltar={onSuccessModalButtonClick}
                modalMsg={'Agenda criada com sucesso!'}
                BtnText={'Ver agendas'}
            />
            <LoadingModal />

        </GeneralContainer>
    </ContainerWithSidebar>;
}

export default CriarAgenda;
